import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api, { setAuthToken, setAuthFailureHandlers } from './utils/api';
import { PartnerEndpoints } from './utils/endpoints';

const AuthContext = createContext();

export const ENTERPRISE_SIGNED_OUT_NOTICE =
  'Your session has ended. If your enterprise was deactivated, contact CashAfrica support.';

const clearStoredSession = () => {
  localStorage.removeItem('authData');
  sessionStorage.removeItem('authData');
};

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(() => {
    const storedData = localStorage.getItem('authData') || sessionStorage.getItem('authData');
    return storedData ? JSON.parse(storedData) : { username: '', token: '' };
  });

  const [tempToken, setTempToken] = useState(null);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAgent, setIsAgent] = useState(false);
  const [subMerchant, setSubMerchant] = useState(null);
  const [enterprise, setEnterprise] = useState(null);
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(false);
  const permissionsInFlight = useRef(false);

  const clearSession = useCallback(() => {
    setPermissions([]);
    setRole(null);
    setUserId(null);
    setIsAgent(false);
    setSubMerchant(null);
    setEnterprise(null);
    setDetailsLoaded(false);
  }, []);

  const refreshPermissions = useCallback(async () => {
    if (permissionsInFlight.current) return;
    permissionsInFlight.current = true;
    try {
      const [permissionsResult, detailsResult] = await Promise.allSettled([
        api.get(PartnerEndpoints.myPermissions),
        api.get(PartnerEndpoints.partnerDetails),
      ]);

      const rejectedStatus = permissionsResult.reason?.response?.status;
      // Only an auth rejection means "this user has no access". A network blip or a
      // 5xx must not be flattened into an empty permission set, or it reads as one.
      const authRejected = rejectedStatus === 401 || rejectedStatus === 403;

      if (permissionsResult.status === 'fulfilled') {
        const { data } = permissionsResult.value.data;
        setPermissions(data.permissions || []);
        setRole(data.role);
        setUserId(data.user_id);
        setIsAgent(Boolean(data.is_agent));
      } else if (authRejected) {
        clearSession();
      }

      if (detailsResult.status === 'fulfilled' && !authRejected) {
        const details = detailsResult.value.data?.data;
        setSubMerchant(details?.sub_merchant ?? null);
        setEnterprise(details?.enterprise ?? null);
        setDetailsLoaded(true);
      }

      setPermissionsError(permissionsResult.status === 'rejected' || detailsResult.status === 'rejected');
    } catch {
      setPermissionsError(true);
    } finally {
      permissionsInFlight.current = false;
      setPermissionsLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    setAuthToken(authData.token);
  }, [authData.token]);

  useEffect(() => {
    if (!authData.token) {
      clearSession();
      setPermissionsError(false);
      setPermissionsLoading(false);
      return;
    }
    setPermissionsLoading(true);
    refreshPermissions();
  }, [authData.token, refreshPermissions, clearSession]);

  const isEnterprise = !isAgent && Boolean(enterprise);
  const isSubMerchant = !isAgent && !isEnterprise && Boolean(subMerchant);

  useEffect(() => {
    setAuthFailureHandlers({
      onUnauthorized: () => {
        // Deactivating an enterprise revokes its token, which is indistinguishable from an expired one.
        if (isEnterprise) sessionStorage.setItem('loginNotice', ENTERPRISE_SIGNED_OUT_NOTICE);
        clearStoredSession();
        window.location.replace('/');
      },
      onDeactivated: (message) => {
        sessionStorage.setItem('loginNotice', message);
        clearStoredSession();
        window.location.replace('/');
      },
      onForbidden: refreshPermissions,
    });
  }, [refreshPermissions, isEnterprise]);

  const hasPermission = (name) => {
    if (!name) return false;
    if (name.endsWith('*')) return permissions.some((p) => p.startsWith(name.slice(0, -1)));
    return permissions.includes(name);
  };

  return (
    <AuthContext.Provider
      value={{
        authData,
        setAuthData,
        tempToken,
        setTempToken,
        isFirstLogin,
        setIsFirstLogin,
        permissions,
        role,
        userId,
        isAgent,
        subMerchant,
        isSubMerchant,
        enterprise,
        isEnterprise,
        detailsLoaded,
        permissionsLoading,
        permissionsError,
        hasPermission,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
